import React, { memo, useCallback, useMemo, useState, useEffect } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import dayjs from 'dayjs'

interface RehireModalProps {
  isModalOpen: boolean
  employeeId: number
  onOk?: () => void
  onCancel?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void
  enums?: Record<string, Array<{ id: number; value: string }>>
}

interface IFormData {
  employment_date?: string
  post_id?: number
  department_id?: number
  office_id?: number
}

interface ISchemaActual {
  resultObject: Record<string, never>
  resultSchema: {
    properties: Record<string, unknown>
    required: string[]
  }
}

export const RehireModal: React.FC<RehireModalProps> = memo((props: RehireModalProps) => {
  const { isModalOpen, employeeId, onOk, onCancel, enums } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<IFormData>({
    employment_date: undefined,
    post_id: undefined,
    department_id: undefined,
    office_id: undefined,
  })

  const actualSchema = useMemo(() => {
    const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual

    if (schemaActual.resultSchema?.properties) {
      const props = schemaActual.resultSchema.properties

      if (enums?.post) {
        props.post_id = {
          ...(props.post_id as object),
          type: 'string',
          oneOf: enums.post.map((item: { id: number; value: string }) => ({
            const: item.id,
            title: item.value,
          })),
          enum: enums.post.map((item: { id: number }) => item.id),
          view: { name: 'select-from-enum' },
          options: { allowClear: true, disabled: loading },
        }
      }
      if (enums?.department) {
        props.department_id = {
          ...(props.department_id as object),
          type: 'string',
          oneOf: enums.department.map((item: { id: number; value: string }) => ({
            const: item.id,
            title: item.value,
          })),
          enum: enums.department.map((item: { id: number }) => item.id),
          view: { name: 'select-from-enum' },
          options: { allowClear: true, disabled: loading },
        }
      }
      if (enums?.office) {
        props.office_id = {
          ...(props.office_id as object),
          type: 'string',
          oneOf: enums.office.map((item: { id: number; value: string }) => ({
            const: item.id,
            title: item.value,
          })),
          enum: enums.office.map((item: { id: number }) => item.id),
          view: { name: 'select-from-enum' },
          options: { allowClear: true, disabled: loading },
        }
      }

      Object.keys(props).forEach(key => {
        const p = props[key] as { options?: Record<string, unknown> }
        if (p && typeof p === 'object') {
          p.options = { ...(p.options || {}), disabled: loading }
        }
      })
    }

    return schemaActual.resultSchema || {}
  }, [enums, loading])

  const handleOk = useCallback(async () => {
    const isRequiredFields = isFormDataRequired(formData, actualSchema)
    if (!isRequiredFields) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните дату приёма.',
      })
      return
    }

    try {
      setLoading(true)
      setError(null)
      const body: Record<string, unknown> = {
        employment_date: formData.employment_date
          ? dayjs(formData.employment_date).toISOString()
          : undefined,
      }
      if (formData.post_id != null) body.post_id = formData.post_id
      if (formData.department_id != null) body.department_id = formData.department_id
      if (formData.office_id != null) body.office_id = formData.office_id

      await fetchAPI(`/api/hr/employees/${employeeId}/rehire`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      onOk?.()
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Не удалось оформить переприём'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [formData, actualSchema, employeeId, onOk])

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setFormData({
        employment_date: undefined,
        post_id: undefined,
        department_id: undefined,
        office_id: undefined,
      })
      setError(null)
      onCancel?.(e)
    },
    [onCancel],
  )

  const onChange = useCallback((data: { formData?: IFormData }) => {
    setFormData(prev => ({ ...prev, ...(data.formData || {}) }))
  }, [])

  useEffect(() => {
    if (isModalOpen) {
      setFormData({
        employment_date: dayjs().format('YYYY-MM-DD'),
        post_id: undefined,
        department_id: undefined,
        office_id: undefined,
      })
      setError(null)
    }
  }, [isModalOpen])

  if (!isModalOpen) return null

  return (
    <Modal
      title={schema.title}
      onOk={handleOk}
      onCancel={handleCancel}
      open={isModalOpen}
      destroyOnClose
      okButtonProps={{ disabled: loading || !formData.employment_date }}
      cancelButtonProps={{ disabled: loading }}
      closable={!loading}
      maskClosable={!loading}
      okText="Оформить"
    >
      {error && (
        <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}
      <Spin spinning={loading}>
        <DefaultJsonForm
          formData={formData}
          schema={actualSchema}
          onChange={onChange}
          orientation="horizontal"
        />
      </Spin>
    </Modal>
  )
})

RehireModal.displayName = 'RehireModal'
